import type { LucideIcon } from 'lucide-react'
import {
  BookOpen, ClipboardCheck, Contact, FileSignature, ListTodo, PackageCheck, Settings2, Target, UserPlus,
} from 'lucide-react'
import { CrmAclAnyOf, CrmAclKeys } from '@/lib/acl'

export type CrmNavPath =
  | '/crm/my-work'
  | '/crm/leads'
  | '/crm/opportunities'
  | '/crm/contacts'
  | '/crm/proposals'
  | '/crm/proposals/approvals'
  | '/crm/handover'
  | '/crm/catalog'
  | '/crm/settings'

export interface CrmNavItem {
  id: string
  to: CrmNavPath
  label: string
  icon: LucideIcon
  group: 'Relationships & growth' | 'Delivery' | 'Setup'
  /** The UI key (or any-of set) that opens the page. Its route's `withPermission` checks the same. */
  access: string | readonly string[]
  /** Paths under `to` that belong to another item, so only one item is active at a time. */
  excludes?: readonly string[]
  /**
   * Set while the page is a preview of a module crm-svc does not serve yet: what the page will do,
   * and which backend module it waits for. Hidden by `VITE_FF_CRM_PREVIEW=false`.
   */
  preview?: { title: string; summary: string; features: readonly string[]; waitsFor: string }
}

const either = (own: string, all: string) => [own, all] as const

/**
 * The CRM tool's tabs (ENT-565), in sidebar order. One source for the CRM sidebar, the `/crm`
 * landing redirect, the preview pages and the CRM entry gate.
 */
export const CRM_NAV: readonly CrmNavItem[] = [
  {
    id: 'my-work',
    to: '/crm/my-work',
    label: 'My work',
    icon: ListTodo,
    group: 'Relationships & growth',
    access: [
      ...either(CrmAclKeys.leadsRead, CrmAclKeys.leadsReadAll),
      ...either(CrmAclKeys.opportunitiesRead, CrmAclKeys.opportunitiesReadAll),
      ...CrmAclAnyOf.proposalsList,
      CrmAclKeys.proposalsApprovalsRead,
      ...either(CrmAclKeys.handoverRead, CrmAclKeys.handoverReadAll),
    ],
    preview: {
      title: 'My work',
      summary: 'Your CRM tasks across inquiries, leads, deals and proposals, grouped by what needs you first.',
      features: [
        'Overdue, due today and waiting-on-client groups',
        'Tasks follow your role: qualification, sales, approval, delivery',
        'Each task names the next required action and opens the record',
      ],
      waitsFor: 'crm-svc task wiring (task framework, ENT-561)',
    },
  },
  {
    id: 'leads',
    to: '/crm/leads',
    label: 'Leads',
    icon: UserPlus,
    group: 'Relationships & growth',
    access: either(CrmAclKeys.leadsRead, CrmAclKeys.leadsReadAll),
    preview: {
      title: 'Leads',
      summary: 'Inquiries to capture, de-duplicate, qualify and convert into opportunities.',
      features: [
        'New inquiries from your web form, the intake API and email arrive as leads with status New',
        'The conversation with each lead shows on its detail page',
        'Import a CSV with a preview, a duplicate review and a receipt',
        'Qualification checklist: business activity, start timeline, visas, language',
        'Convert a qualified lead into an opportunity',
      ],
      waitsFor: 'crm-svc Leads module (LLD-L4)',
    },
  },
  {
    id: 'opportunities',
    to: '/crm/opportunities',
    label: 'Opportunities',
    icon: Target,
    group: 'Relationships & growth',
    access: either(CrmAclKeys.opportunitiesRead, CrmAclKeys.opportunitiesReadAll),
    preview: {
      title: 'Opportunities',
      summary: 'Deals moving through your pipeline, as a board or a table.',
      features: [
        'Board by stage, using the stages your agency sets, with deals that have gone quiet flagged',
        'Stage checks that say what is missing before a deal can move',
        'Proposals linked to the deal they price',
      ],
      waitsFor: 'crm-svc Opportunities module (LLD-L5)',
    },
  },
  {
    id: 'contacts',
    to: '/crm/contacts',
    label: 'Contacts',
    icon: Contact,
    group: 'Relationships & growth',
    access: either(CrmAclKeys.contactsRead, CrmAclKeys.contactsReadAll),
    preview: {
      title: 'Contacts',
      summary: 'Client accounts and the people you deal with at each one.',
      features: [
        'Accounts and people, each with an owner and a status',
        'Every deal, proposal and request for an account in one place',
        'Personal data shown only to roles that may see it',
      ],
      waitsFor: 'crm-svc Accounts and contacts module (LLD-L3)',
    },
  },
  {
    id: 'proposals',
    to: '/crm/proposals',
    label: 'Proposals',
    icon: FileSignature,
    group: 'Relationships & growth',
    access: CrmAclAnyOf.proposalsList,
    excludes: ['/crm/proposals/approvals'],
  },
  {
    id: 'approvals',
    to: '/crm/proposals/approvals',
    label: 'Approval queue',
    icon: ClipboardCheck,
    group: 'Relationships & growth',
    access: CrmAclKeys.proposalsApprovalsRead,
  },
  {
    id: 'handover',
    to: '/crm/handover',
    label: 'Delivery handover',
    icon: PackageCheck,
    group: 'Delivery',
    access: either(CrmAclKeys.handoverRead, CrmAclKeys.handoverReadAll),
    preview: {
      title: 'Delivery handover',
      summary: 'Won deals handed from sales to the delivery team.',
      features: [
        'One handover line per service sold',
        'Delivery accepts a line or returns it with a reason',
        'Accepted lines start the incorporation engagement',
      ],
      waitsFor: 'crm-svc Delivery module (gate G2, not designed yet)',
    },
  },
  {
    id: 'catalog',
    to: '/crm/catalog',
    label: 'Service catalog',
    icon: BookOpen,
    group: 'Setup',
    access: CrmAclKeys.catalogRead,
    preview: {
      title: 'Service catalog',
      summary: 'The services and packages you sell, and their prices.',
      features: [
        'Services and packages, each with a stable key',
        'Price versions per currency, with an approval before a price goes live',
        'Authority fee tables per jurisdiction',
      ],
      waitsFor: 'crm-svc Catalog and pricing module (LLD-L7)',
    },
  },
  {
    id: 'settings',
    to: '/crm/settings',
    label: 'CRM settings',
    icon: Settings2,
    group: 'Setup',
    access: CrmAclKeys.settingsRead,
    preview: {
      title: 'CRM settings',
      summary: 'How the CRM works for your agency.',
      features: [
        'Your own pipeline stages, and the checks on each move',
        'Routing rules and speed-to-lead targets',
        'Who holds which CRM role and add-on',
      ],
      waitsFor: 'crm-svc settings screens (LLD-L2 settings exist; screens not built)',
    },
  },
]

export const CRM_NAV_GROUPS = ['Relationships & growth', 'Delivery', 'Setup'] as const

export function crmNavItem(id: string): CrmNavItem {
  const item = CRM_NAV.find((i) => i.id === id)
  if (!item) throw new Error(`Unknown CRM nav item: ${id}`)
  return item
}

/** The items this caller sees: holds the page's key, and preview pages only while the flag is on. */
export function visibleCrmNav(can: (key: string | readonly string[]) => boolean, previewEnabled: boolean): CrmNavItem[] {
  return CRM_NAV.filter((i) => (previewEnabled || !i.preview) && can(i.access))
}
